import React, { useState, useEffect } from 'react';
import { X, Share2, Trash2, UserPlus, Shield, Eye, Edit3 } from 'lucide-react';
import axios from 'axios';
import { API_URL as API_BASE } from '../../config.js';

export default function ShareDashboardModal({ dashboard, darkMode, onClose }) {
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('viewer');
  const [maxSessions, setMaxSessions] = useState(1);
  const [shares, setShares] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fetchingShares, setFetchingShares] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const token = localStorage.getItem('token') || sessionStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const theme = darkMode ? {
    bg: '#0f1117', card: '#1a1d29', text: '#e5e7eb', textSecondary: '#9ca3af',
    border: '#374151', accent: '#6366f1', input: '#0f1117', danger: '#ef4444',
    success: '#10b981', hover: '#232837'
  } : {
    bg: '#ffffff', card: '#f9fafb', text: '#111827', textSecondary: '#6b7280',
    border: '#e5e7eb', accent: '#6366f1', input: '#ffffff', danger: '#ef4444',
    success: '#10b981', hover: '#f3f4f6'
  };

  useEffect(() => {
    fetchShares();
  }, [dashboard?.id]);

  const fetchShares = async () => {
    if (!dashboard?.id) return;
    setFetchingShares(true);
    try {
      const res = await axios.get(`${API_BASE}/dashboards/${dashboard.id}/shares`, { headers });
      setShares(res.data.shares || []);
    } catch {
      setShares([]);
    }
    setFetchingShares(false);
  };

  const handleShare = async () => {
    if (!email.trim()) { setError('Please enter an email address'); return; }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) { setError('Invalid email address'); return; }
    setError(''); setLoading(true);
    try {
      const res = await axios.post(
        `${API_BASE}/dashboards/${dashboard.id}/share`,
        { email: email.trim(), role, maxSessions: Number(maxSessions) },
        { headers }
      );
      setShares(res.data.shares || []);
      setSuccess(`Shared with ${email.trim()}`);
      setEmail('');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to share dashboard');
    }
    setLoading(false);
  };

  const handleRevoke = async (shareEmail) => {
    try {
      const res = await axios.delete(
        `${API_BASE}/dashboards/${dashboard.id}/shares/${encodeURIComponent(shareEmail)}`,
        { headers }
      );
      setShares(res.data.shares || []);
    } catch {
      setError('Failed to revoke access');
    }
  };

  const inputStyle = {
    padding: '8px 12px', background: theme.input, border: `1px solid ${theme.border}`,
    borderRadius: '8px', color: theme.text, fontSize: '13px', outline: 'none',
    width: '100%', boxSizing: 'border-box'
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 99999, padding: '16px'
    }}>
      <div style={{
        background: theme.bg, borderRadius: '14px', width: '100%', maxWidth: '500px',
        boxShadow: '0 20px 60px rgba(0,0,0,0.4)', border: `1px solid ${theme.border}`,
        display: 'flex', flexDirection: 'column', maxHeight: '90vh'
      }}>
        {/* Header */}
        <div style={{ padding: '18px 20px', borderBottom: `1px solid ${theme.border}`, display: 'flex', alignItems: 'center', gap: '10px' }}>
          <Share2 size={18} color={theme.accent} />
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: '700', fontSize: '15px', color: theme.text }}>Share Dashboard</div>
            <div style={{ fontSize: '12px', color: theme.textSecondary, marginTop: '2px' }}>{dashboard?.name}</div>
          </div>
          <button onClick={onClose} style={{ background: 'transparent', border: 'none', color: theme.textSecondary, cursor: 'pointer', padding: '4px' }}>
            <X size={16} />
          </button>
        </div>

        <div style={{ padding: '20px', overflow: 'auto', flex: 1 }}>
          {/* Share form */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginBottom: '20px' }}>
            <div>
              <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: theme.textSecondary, marginBottom: '6px' }}>
                Email Address
              </label>
              <input
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleShare()}
                placeholder="user@example.com"
                style={inputStyle}
              />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
              <div>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: theme.textSecondary, marginBottom: '6px' }}>
                  Access Level
                </label>
                <select value={role} onChange={e => setRole(e.target.value)} style={{ ...inputStyle }}>
                  <option value="viewer">Viewer (read-only)</option>
                  <option value="editor">Editor (can modify)</option>
                </select>
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: theme.textSecondary, marginBottom: '6px' }}>
                  Max Concurrent Sessions
                </label>
                <input
                  type="number"
                  value={maxSessions}
                  onChange={e => setMaxSessions(Math.max(1, parseInt(e.target.value) || 1))}
                  min={1} max={10}
                  style={inputStyle}
                />
              </div>
            </div>

            <div style={{ fontSize: '11px', color: theme.textSecondary, background: theme.card, padding: '8px 12px', borderRadius: '6px', border: `1px solid ${theme.border}` }}>
              <strong style={{ color: theme.text }}>Session limit:</strong> This email can be logged in from at most <strong style={{ color: theme.accent }}>{maxSessions}</strong> device{maxSessions !== 1 ? 's' : ''} simultaneously.
            </div>

            {error && <div style={{ fontSize: '12px', color: theme.danger, padding: '8px 12px', background: theme.danger + '18', borderRadius: '6px' }}>{error}</div>}
            {success && <div style={{ fontSize: '12px', color: theme.success, padding: '8px 12px', background: theme.success + '18', borderRadius: '6px' }}>{success}</div>}

            <button
              onClick={handleShare}
              disabled={loading}
              style={{
                padding: '10px 16px', background: theme.accent, border: 'none', borderRadius: '8px',
                color: 'white', cursor: loading ? 'not-allowed' : 'pointer', fontSize: '13px', fontWeight: '600',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
                opacity: loading ? 0.7 : 1
              }}
            >
              <UserPlus size={14} />
              {loading ? 'Sharing…' : 'Share Dashboard'}
            </button>
          </div>

          {/* Current shares list */}
          <div>
            <div style={{ fontSize: '12px', fontWeight: '600', color: theme.textSecondary, marginBottom: '10px', display: 'flex', alignItems: 'center', gap: '6px' }}>
              <Shield size={12} />
              CURRENT ACCESS
              {shares.length > 0 && (
                <span style={{ background: theme.accent + '22', color: theme.accent, borderRadius: '10px', padding: '1px 7px', fontSize: '11px' }}>
                  {shares.length}
                </span>
              )}
            </div>

            {fetchingShares ? (
              <div style={{ fontSize: '12px', color: theme.textSecondary, padding: '12px', textAlign: 'center' }}>Loading…</div>
            ) : shares.length === 0 ? (
              <div style={{ fontSize: '12px', color: theme.textSecondary, padding: '12px', textAlign: 'center', border: `1px dashed ${theme.border}`, borderRadius: '8px' }}>
                Not shared with anyone yet
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                {shares.map(share => (
                  <div key={share.email} style={{
                    display: 'flex', alignItems: 'center', gap: '10px',
                    padding: '10px 12px', background: theme.card, borderRadius: '8px',
                    border: `1px solid ${theme.border}`
                  }}>
                    <div style={{
                      width: '32px', height: '32px', borderRadius: '50%',
                      background: theme.accent + '22', color: theme.accent,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: '13px', fontWeight: '700', flexShrink: 0
                    }}>
                      {share.email[0].toUpperCase()}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: '13px', fontWeight: '600', color: theme.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {share.email}
                      </div>
                      <div style={{ fontSize: '11px', color: theme.textSecondary, display: 'flex', alignItems: 'center', gap: '8px', marginTop: '2px' }}>
                        <span style={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
                          {share.role === 'editor' ? <Edit3 size={10} /> : <Eye size={10} />}
                          {share.role}
                        </span>
                        <span>·</span>
                        <span>{share.maxSessions} session{share.maxSessions !== 1 ? 's' : ''} max</span>
                      </div>
                    </div>
                    <button
                      onClick={() => handleRevoke(share.email)}
                      title="Revoke access"
                      style={{
                        background: 'transparent', border: 'none', cursor: 'pointer',
                        color: theme.textSecondary, padding: '4px', borderRadius: '4px',
                        display: 'flex', alignItems: 'center', flexShrink: 0
                      }}
                      onMouseEnter={e => { e.currentTarget.style.color = theme.danger; }}
                      onMouseLeave={e => { e.currentTarget.style.color = theme.textSecondary; }}
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
